/**
 * resolveBuilderMarginContext — the one margin-context builder, shared by the
 * quote builder and the change-order builder.
 *
 * IDI-457 (parent IDI-455). Both builders assemble the same three-key context
 * for the engine's cascade (`quote-builder/marginEngine.js resolveMargin`:
 * lineMargin → categoryMargin → customerMargin → customerTypeMargin →
 * quoteMargin), but they built it from different sources and disagreed:
 *
 *   - QuoteBuilderPage resolved `customerTypeMargin` from the `customerTypes`
 *     list loaded from Settings (IDI-379 proved Settings stores it under
 *     `customerTypes`, camelCase — `customer_types` is ABSENT on tenant 1).
 *   - ChangeOrderBuilderPage resolved it from
 *     `getSettingsOptions('customer_types')` — the snake_case key — so the
 *     lookup matched nothing and the customer-type margin NEVER reached a
 *     change order. That is the IDI-99 "dead context keys" symptom.
 *
 * One function, one source of truth, so a change order prices a line exactly
 * as the quote it amends does.
 */
import { normalizeMarginValue } from '../quote-builder/quotePrefill';

/**
 * Find a customer type's default margin in the tenant's Settings list.
 *
 * Accepts both shapes seen live: `value`/`id`/`name` as the identifier, and
 * `default_margin_pct`/`defaultMarginPct` as the margin. Returns null when
 * the type or its margin is unset, so the engine cascades to the next level.
 *
 * @param {string|null|undefined} customerType
 * @param {Array<object>} customerTypes
 * @returns {number|null}
 */
export function resolveCustomerTypeMargin(customerType, customerTypes) {
  if (!customerType || !Array.isArray(customerTypes) || customerTypes.length === 0) return null;
  const match = customerTypes.find((t) => (
    t && (t.value === customerType || t.id === customerType || t.name === customerType)
  ));
  if (!match) return null;
  return normalizeMarginValue(match.default_margin_pct ?? match.defaultMarginPct);
}

/**
 * Build the margin context the engine and the Add-Item wizard both consume.
 *
 * @param {object}   params
 * @param {number|null} params.customerMargin  Level 2 — customer-specific default.
 * @param {string|null} params.customerType    The customer's type identifier.
 * @param {Array<object>} params.customerTypes Tenant Settings list (from useBuilderCatalog).
 * @param {number|null} params.overallMargin   Level 4 — the document's own margin.
 * @returns {{customerMargin: number|null, customerTypeMargin: number|null, quoteMargin: number|null}}
 */
export function resolveBuilderMarginContext({
  customerMargin = null,
  customerType = null,
  customerTypes = [],
  overallMargin = null,
} = {}) {
  return {
    customerMargin: normalizeMarginValue(customerMargin),
    customerTypeMargin: resolveCustomerTypeMargin(customerType, customerTypes),
    // The engine's level-5 key. `overallMargin` is its deprecated alias —
    // pass `quoteMargin` only, so a caller can't set both and disagree.
    quoteMargin: normalizeMarginValue(overallMargin),
  };
}

export default resolveBuilderMarginContext;
